import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import { FaFilePdf, FaDownload } from 'react-icons/fa';
import API_BASE_URL from '../../../config/api'

// Styled modal component (matches GoalReviews design)
const Modal = ({ isOpen, onClose, children, style }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(30, 41, 59, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        padding: '32px 28px 24px 28px',
        borderRadius: 16,
        minWidth: 340,
        maxWidth: 640,
        boxShadow: '0 8px 32px rgba(30,41,59,0.18)',
        position: 'relative',
        fontFamily: 'inherit',
        animation: 'modalFadeIn 0.2s',
        ...style,
      }}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', fontSize: 22, color: '#64748b', cursor: 'pointer', fontWeight: 600, lineHeight: 1 }}
          aria-label="Close"
        >
          &times;
        </button>
        {children}
      </div>
      <style>{`
        @keyframes modalFadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

const MyGoalReviews = () => {
  const baseUrl = API_BASE_URL;
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewGoal, setViewGoal] = useState(null);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/key-result-areas/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // collect completed goals across KRAs
        const allGoals = (Array.isArray(response.data) ? response.data : [])
          .flatMap(kra => Array.isArray(kra.goals) ? kra.goals : [])
          .filter(goal => goal.status === 'complete' || goal.status === 'completed');
        setGoals(allGoals);
        // build year options
        const years = Array.from(new Set(allGoals.map(g => g?.year).filter(y => y !== undefined && y !== null))).sort((a, b) => a - b);
        setAvailableYears(years);
        const effectiveYear = years.length && years.includes(selectedYear) ? selectedYear : (years[years.length - 1] || selectedYear);
        setSelectedYear(effectiveYear);
        setFilteredGoals(allGoals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === effectiveYear : true));
      } catch (err) {
        setError('Failed to fetch goals.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, []);

  useEffect(() => {
    if (goals.length > 0) {
      setFilteredGoals(goals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === selectedYear : true));
    }
  }, [selectedYear, goals]);

  const handleViewGoal = (goal) => {
    setViewGoal(goal);
    setViewModalOpen(true);
  };

  // Prepare data for report
  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });
    const totalHod = filteredGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0);
    const totalMetric = filteredGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0);
    const percentage = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';
    return { year: selectedYear, kras: Object.entries(kraMap), totalHod, totalMetric, percentage, goals: filteredGoals };
  };

  // Generate Professional PDF Report
  const generatePDF = async () => {
    try {
      const report = generateReportData();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');

      const primary = [59, 130, 246];
      const dark = [55, 65, 81];
      const light = [156, 163, 175];

      const wrap = (text, maxWidth) => {
        const words = String(text || '').split(' ');
        const lines = []; let line = '';
        words.forEach(w => { const t = line ? (line + ' ' + w) : w; if (doc.getTextWidth(t) > maxWidth && line) { lines.push(line); line = w; } else { line = t; } });
        if (line) lines.push(line); return lines;
      };

      const drawTable = (headers, rows, startY, maxWidth = 180) => {
        const colWidths = [70, 40, 30, 30, 10];
        const headerH = 10; const rowH = 8; let x = 15;
        doc.setFillColor(...primary); doc.rect(15, startY, maxWidth, headerH, 'F');
        headers.forEach((h, i) => { doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.text(h, x + 2, startY + 6); x += colWidths[i]; });
        let y = startY + headerH; doc.setTextColor(0, 0, 0); doc.setFont(undefined, 'normal'); doc.setFontSize(9);
        rows.forEach((row, rIdx) => {
          if (rIdx % 2 === 0) { doc.setFillColor(249, 250, 251); doc.rect(15, y, maxWidth, rowH, 'F'); }
          let xx = 15; let maxLines = 1;
          row.forEach((cell, i) => { const wl = wrap(cell, colWidths[i] - 4); maxLines = Math.max(maxLines, wl.length); wl.forEach((ln, li) => { doc.text(ln, xx + 2, y + 5 + (li * 3.5)); }); xx += colWidths[i]; });
          y += Math.max(rowH, maxLines * 3.5 + 2);
        });
        return y;
      };

      let y = 20;
      doc.setTextColor(...primary); doc.setFontSize(18); doc.setFont(undefined, 'bold');
      doc.text('Performance Report', 105, y, { align: 'center' });
      y += 12; doc.setTextColor(...dark); doc.setFontSize(14); doc.setFont(undefined, 'normal');
      doc.text(`Year: ${report.year}`, 105, y, { align: 'center' });
      y += 16; doc.setFillColor(248, 250, 252); doc.rect(15, y, 180, 25, 'F'); doc.setDrawColor(...primary); doc.rect(15, y, 180, 25);
      y += 8; doc.setTextColor(...dark); doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.text('Summary', 20, y);
      y += 7; doc.setFontSize(11); doc.setFont(undefined, 'normal'); doc.text(`Total Final Score: ${report.totalHod} / ${report.totalMetric}`, 20, y);
      y += 5; doc.text(`Performance: ${report.percentage}%`, 20, y);
      y += 15;

      report.kras.forEach(([kraName, kraGoals]) => {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFillColor(...primary); doc.rect(15, y, 180, 12, 'F'); doc.setTextColor(255, 255, 255); doc.setFontSize(12); doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kraName}`, 20, y + 8); y += 15;
        const headers = ['Activity/KPI', 'Standard', 'Metric', 'COO', ''];
        const rows = kraGoals.map(g => [
          g.activity || 'N/A',
          g.performance_standard || 'N/A',
          (g.metric !== undefined ? g.metric : '-'),
          (g.hod_score !== undefined ? g.hod_score : '-'),
          ''
        ]);
        const kraTotal = kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0);
        const kraMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0);
        rows.push(['KRA Total', '', `${kraMetric}`, `${kraTotal}`, '']);
        y = drawTable(headers, rows, y);
        y += 10;
      });

      const pages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i); doc.setTextColor(...light); doc.setFontSize(8);
        doc.text('Generated by PMS', 15, 290);
        doc.text(`Page ${i} of ${pages}`, 195, 290, { align: 'right' });
      }
      const filename = `KPI Reviews Report ${selectedYear}.pdf`;
      doc.save(filename);
    } catch (e) {
      alert('Failed to generate PDF');
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="hr-content-wrapper" style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
            padding: '24px',
            marginBottom: 24 
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: '20px'
            }}>
              <div style={{ color: '#070987' }}>
                <h3 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>KPIs Reviews</h3>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>Review your completed KPIs and performance metrics</p>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 16,
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 14, color: '#334155', fontWeight: '500' }}>Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    style={{ 
                      padding: '8px 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: 8, 
                      background: '#fff', 
                      color: '#374151', 
                      fontSize: 14, 
                      cursor: 'pointer',
                      minWidth: '100px',
                      fontWeight: '500'
                    }}
                    title="Filter by year"
                  >
                    {(availableYears.length ? availableYears : [selectedYear]).sort((a, b) => a - b).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                {filteredGoals.length > 0 && (
                  <button 
                    onClick={generatePDF} 
                    title="Download PDF Report" 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8, 
                      background: '#dc2626', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: 8, 
                      padding: '10px 16px', 
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#b91c1c'}
                    onMouseOut={(e) => e.target.style.background = '#dc2626'}
                  >
                    <FaFilePdf style={{ fontSize: 16 }} />
                    <FaDownload style={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
                    </div>
                    </div>

        {loading ? (
          <div style={{ color: '#64748b', fontSize: 18 }}>Loading...</div>
        ) : error ? (
          <div style={{ color: '#ef4444', background: '#fef2f2', borderRadius: 4, padding: '10px 18px', fontSize: 16 }}>{error}</div>
          ) : filteredGoals.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 18 }}>No completed KPIs found.</div>
        ) : (
            (() => {
              const kraMap = {};
              filteredGoals.forEach(g => {
                const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
                if (!kraMap[kraName]) kraMap[kraName] = [];
                kraMap[kraName].push(g);
              });
              const grouped = Object.entries(kraMap);
              return (
                <>
                  <div style={{ 
                    background: '#fff', 
                    borderRadius: 12, 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
                    padding: '24px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse', 
                        background: '#fff',
                        minWidth: '800px'
                      }}>
                <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ 
                              padding: '16px 20px', 
                              textAlign: 'left', 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#374151',
                              borderBottom: '2px solid #e5e7eb',
                              whiteSpace: 'nowrap'
                            }}>KRA</th>
                            <th style={{ 
                              padding: '16px 20px', 
                              textAlign: 'left', 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#374151',
                              borderBottom: '2px solid #e5e7eb',
                              whiteSpace: 'nowrap'
                            }}>Activity / KPI</th>
                            <th style={{ 
                              padding: '16px 20px', 
                              textAlign: 'left', 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#374151',
                              borderBottom: '2px solid #e5e7eb',
                              whiteSpace: 'nowrap'
                            }}>Performance Standard</th>
                            <th style={{ 
                              padding: '16px 20px', 
                              textAlign: 'center', 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#374151',
                              borderBottom: '2px solid #e5e7eb',
                              whiteSpace: 'nowrap'
                            }}>Metric</th>
                            <th style={{ 
                              padding: '16px 20px', 
                              textAlign: 'center', 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#374151',
                              borderBottom: '2px solid #e5e7eb',
                              whiteSpace: 'nowrap'
                            }}>Self Score</th>
                            <th style={{ 
                              padding: '16px 20px', 
                              textAlign: 'center', 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#374151',
                              borderBottom: '2px solid #e5e7eb',
                              whiteSpace: 'nowrap'
                            }}>COO Score</th>
                  </tr>
                </thead>
                <tbody>
                          {grouped.map(([kra, kraGoals], idx) => (
                            <React.Fragment key={kra || idx}>
                              {kraGoals.map((goal, gidx) => (
                                <tr key={goal.id || gidx} style={{ 
                                  borderBottom: '1px solid #f3f4f6',
                                  transition: 'background-color 0.2s'
                                }}>
                                  <td style={{ 
                                    padding: '16px 20px', 
                                    fontSize: '14px', 
                                    fontWeight: '600',
                                    color: '#1f2937',
                                    background: gidx === 0 ? '#f9fafb' : 'transparent',
                                    borderLeft: gidx === 0 ? '4px solid #3b82f6' : 'none'
                                  }}>
                                    {gidx === 0 ? kra : ''}
                                  </td>
                                  <td style={{ 
                                    padding: '16px 20px', 
                                    fontSize: '14px', 
                                    color: '#374151',
                                    maxWidth: '300px',
                                    wordWrap: 'break-word'
                                  }}>
                                    {goal.activity || '[No Activity]'}
                                  </td>
                                  <td style={{ 
                                    padding: '16px 20px', 
                                    fontSize: '14px', 
                                    color: '#374151',
                                    maxWidth: '300px',
                                    wordWrap: 'break-word'
                                  }}>
                                    {goal.performance_standard !== undefined ? goal.performance_standard : '-'}
                                  </td>
                                  <td style={{ 
                                    padding: '16px 20px', 
                                    textAlign: 'center', 
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#1f2937'
                                  }}>
                                    {goal.metric !== undefined ? goal.metric : '-'}
                                  </td>
                                  <td style={{ 
                                    padding: '16px 20px', 
                                    textAlign: 'center', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    color: '#3b82f6'
                                  }}>
                                    {goal.score !== undefined ? goal.score : '-'}
                                  </td>
                                  <td style={{ 
                                    padding: '16px 20px', 
                                    textAlign: 'center', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    color: '#059669'
                                  }}>
                                    {goal.hod_score !== undefined ? goal.hod_score : '-'}
                                  </td>
                    </tr>
                              ))}
                              <tr style={{ 
                                background: '#f8fafc', 
                                borderTop: '2px solid #e5e7eb',
                                fontWeight: '700'
                              }}>
                                <td style={{ 
                                  padding: '16px 20px', 
                                  fontSize: '14px',
                                  color: '#1f2937',
                                  fontWeight: '700'
                                }}>
                                  {kra} Total
                                </td>
                                <td colSpan={2} style={{ 
                                  textAlign: 'right', 
                                  padding: '16px 20px', 
                                  fontSize: '14px',
                                  color: '#6b7280'
                                }}>
                                  Subtotal
                                </td>
                                <td style={{ 
                                  textAlign: 'center', 
                                  padding: '16px 20px', 
                                  fontSize: '14px', 
                                  fontWeight: '800', 
                                  color: '#1f2937'
                                }}>
                                  {kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)}
                                </td>
                                <td style={{ 
                                  textAlign: 'center', 
                                  padding: '16px 20px', 
                                  fontSize: '14px', 
                                  fontWeight: '800', 
                                  color: '#1f2937'
                                }}>
                                  {kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0)}
                                </td>
                                <td style={{ 
                                  textAlign: 'center', 
                                  padding: '16px 20px', 
                                  fontSize: '14px', 
                                  fontWeight: '800', 
                                  color: '#1f2937'
                                }}>
                                  {kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0)} / {kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)}
                                </td>
                              </tr>
                            </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
                  </div>
                  {filteredGoals.length > 0 && (() => {
                    const totalHod = filteredGoals.reduce((sum, g) => sum + (Number(g.hod_score) || 0), 0);
                    const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
                    const pct = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';
                    return (
                      <div style={{ 
                        background: '#fff', 
                        borderRadius: 12, 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
                        padding: '24px',
                        marginTop: '24px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '16px'
                        }}>
                          <div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>Performance Summary</h4>
                            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Overall performance metrics for {selectedYear}</p>
                          </div>
                          <div style={{
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '24px',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>Total Score</div>
                              <div style={{ fontSize: '20px', fontWeight: '800', color: '#1f2937' }}>{totalHod} / {totalMetric}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>Performance</div>
                              <div style={{
                                background: pct >= 80 ? '#dcfce7' : pct >= 60 ? '#fef3c7' : '#fee2e2',
                                color: pct >= 80 ? '#166534' : pct >= 60 ? '#92400e' : '#991b1b',
                                fontWeight: '800',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                border: pct >= 80 ? '1px solid #bbf7d0' : pct >= 60 ? '1px solid #fde68a' : '1px solid #fecaca'
                              }}>{pct}%</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              );
            })()
        )}
        </div>        
      </div>
      <Modal style={{ minWidth: 640 }} isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)}>
        <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 700, fontSize: 22, color: '#0f172a', letterSpacing: 0.2 }}>Goal Details</h2>
        {viewGoal && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 640, background: '#f8fafc', borderRadius: 12, boxShadow: '0 1px 4px rgba(30,41,59,0.06)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {[
                { label: 'Activity / KPI', value: viewGoal.activity },
                { label: 'Performance Standard / Target', value: viewGoal.performance_standard },
                { label: 'Metric', value: viewGoal.metric || viewGoal.score },
                { label: 'Self Score', value: viewGoal.score },
                { label: 'COO Score', value: viewGoal.hod_score !== undefined ? viewGoal.hod_score : '-' },
              ].map((row, idx, arr) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 20px',
                    background: idx % 2 === 0 ? '#fff' : '#f1f5f9',
                    borderBottom: idx !== arr.length - 1 ? '1px solid #e2e8f0' : 'none',
                    fontSize: 16,
                    transition: 'background 0.18s',
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = '#e0e7ef')}
                  onMouseOut={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f1f5f9')}
                >
                  <span style={{ color: '#334155', fontWeight: 500 }}>{row.label}</span>
                  <span style={{ color: '#0f172a', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{row.value}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
      
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .hr-content-wrapper {
            padding: 16px 12px !important;
          }
          .page-header .header-content {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
          }
          .page-header .header-content > div:last-child {
            width: 100% !important;
            justify-content: space-between !important;
          }
        }
        @media (max-width: 480px) {
          .page-header .header-content > div:last-child {
            flex-direction: column !important;
            gap: 12px !important;
            align-items: stretch !important;
          }
          .page-header .header-content > div:last-child > div {
            width: 100% !important;
            justify-content: center !important;
          }
        }
      `}</style>
    </>
  );
}

export default MyGoalReviews;
